/**
 * -----------------------------------------------------------------------------
 * useRasterLayer
 * -----------------------------------------------------------------------------
 *
 * PURPOSE:
 * Render raster tile source (e.g., webtiles) on Mapbox with optional outline.
 *
 * REPLACES:
 *   - showOrderDetails()
 *   - webtile-source
 *   - webtile-outline-source
 *   - contextmenu binding on raster polygon
 *
 * FEATURES:
 *   - Adds raster source
 *   - Adds raster layer
 *   - Optional outline GeoJSON layer
 *   - Optional right-click handler
 *   - Safe cleanup
 *   - Style reload safe
 *
 * DOES NOT HANDLE:
 *   - STAC vector layers
 *   - Orbit lines
 *   - Draw controls
 *
 * USAGE:
 *
 * useRasterLayer(mapRef, {
 *   id: "preview",
 *   tileUrl: "...",
 *   outlineFeature: selectedFeature,
 *   onRightClick: (feature, e) => {...}
 * });
 *
 * -----------------------------------------------------------------------------
 */

import { useEffect } from "react";

const useRasterLayer = (
  mapRef,
  { id, tileUrl, outlineFeature, onRightClick }
) => {
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const stableMap = map; // store stable reference

    const removeLayers = () => {
      try {
        if (!stableMap || stableMap._removed || !stableMap.isStyleLoaded())
          return;

        [
          `${id}-outline-fill`,
          `${id}-outline-line`,
          `${id}-raster-layer`,
        ].forEach((layerId) => {
          if (stableMap.getLayer(layerId)) {
            stableMap.removeLayer(layerId);
          }
        });

        [`${id}-outline`, `${id}-raster`].forEach((sourceId) => {
          if (stableMap.getSource(sourceId)) {
            stableMap.removeSource(sourceId);
          }
        });
      } catch (err) {
        console.warn("Raster cleanup safely skipped:", err);
      }
    };

    if (!tileUrl) {
      removeLayers();
      return;
    }

    const loadLayer = () => {
      if (!stableMap || stableMap._removed) return;

      // Outline
      if (outlineFeature) {
        if (!stableMap.getSource(`${id}-outline`)) {
          stableMap.addSource(`${id}-outline`, {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [outlineFeature],
            },
          });
        } else {
          stableMap.getSource(`${id}-outline`).setData({
            type: "FeatureCollection",
            features: [outlineFeature],
          });
        }

        if (!stableMap.getLayer(`${id}-outline-fill`)) {
          stableMap.addLayer({
            id: `${id}-outline-fill`,
            type: "fill",
            source: `${id}-outline`,
            paint: {
              "fill-color": "#00FF00",
              "fill-opacity": 0.3,
            },
          });
        }

        if (!stableMap.getLayer(`${id}-outline-line`)) {
          stableMap.addLayer({
            id: `${id}-outline-line`,
            type: "line",
            source: `${id}-outline`,
            paint: {
              "line-color": "#00FF00",
              "line-width": 2,
            },
          });
        }

        if (onRightClick) {
          stableMap.on("contextmenu", `${id}-outline-fill`, (e) => {
            e.preventDefault();
            onRightClick(outlineFeature, e);
          });
        }
      }
      
      // Raster source
      if (!stableMap.getSource(`${id}-raster`)) {
        stableMap.addSource(`${id}-raster`, {
          type: "raster",
          tiles: [tileUrl],
          tileSize: 256,
        });
      }

      if (!stableMap.getLayer(`${id}-raster-layer`)) {
        stableMap.addLayer({
          id: `${id}-raster-layer`,
          type: "raster",
          source: `${id}-raster`,
        });
      }

    };

    if (!stableMap.isStyleLoaded()) {
      stableMap.once("styledata", loadLayer);
    } else {
      loadLayer();
    }

    return () => {
      removeLayers();
    };
  }, [tileUrl, outlineFeature, id]);
};

export default useRasterLayer;